import { checkPhoneRegistered, getPersonalInfo, login, logout, register, sendForgotPasswordSmsCode, sendLoginSmsCode, sendRegisterSmsCode, resetPassword, sendWechatBindPhoneSmsCode, wechatBindPhone } from '@/api/authApi'

function normalizePhone(payload) {
  return (payload && (payload.phone || payload.mobile)) || ''
}

function getSmsLimitKey(scene, phone) {
  return `smsLimit:${scene}:${phone}`
}

function canSendSms({ scene, phone, maxCount = 3, windowMs = 5 * 60 * 1000 }) {
  const p = String(phone || '').trim()
  if (!p) return { ok: false, message: '请输入手机号' }
  const key = getSmsLimitKey(scene, p)
  const raw = localStorage.getItem(key)
  let arr = []
  if (raw) {
    try {
      const parsed = JSON.parse(raw)
      arr = Array.isArray(parsed) ? parsed : []
    } catch (e) {
      arr = []
    }
  }
  const now = Date.now()
  const keep = arr.filter(ts => typeof ts === 'number' && now - ts < windowMs)
  if (keep.length >= maxCount) {
    return { ok: false, message: '同一手机号5分钟内最多获取3次验证码，请稍后再试' }
  }
  return { ok: true, key, now, keep }
}

function markSmsSent({ key, now, keep }) {
  const next = Array.isArray(keep) ? keep.slice() : []
  next.push(now)
  localStorage.setItem(key, JSON.stringify(next))
}

// 初始化状态
function getInitialState() {
  const token = localStorage.getItem('token')
  const userInfoStr = localStorage.getItem('userInfo')
  
  if (token) {
    if (userInfoStr) {
      try {
        const userInfo = JSON.parse(userInfoStr)
        const name = userInfo.realName || userInfo.nickname || userInfo.username || userInfo.phone || '用户'
        return {
          isLoggedIn: true,
          userInfo: userInfo,
          username: name,
          wechatTempToken: null
        }
      } catch (error) {
        localStorage.removeItem('userInfo')
      }
    }
    return {
      isLoggedIn: true,
      userInfo: null,
      username: '用户',
      wechatTempToken: null
    }
  }
  
  return {
    isLoggedIn: false,
    userInfo: null,
    username: '',
    wechatTempToken: null
  }
}

const auth = {
  namespaced: true,
  state: getInitialState(),
  mutations: {
    // 设置登录状态
    SET_LOGIN_STATUS(state, status) {
      state.isLoggedIn = status
    },
    // 设置用户信息
    SET_USER_INFO(state, userInfo) {
      state.userInfo = userInfo
    },
    // 设置用户名
    SET_USERNAME(state, username) {
      state.username = username
    },
    SET_WECHAT_TEMP_TOKEN(state, tempToken) {
      state.wechatTempToken = tempToken || null
    },
    // 清除用户信息
    CLEAR_USER_INFO(state) {
      state.isLoggedIn = false
      state.userInfo = null
      state.username = ''
      state.wechatTempToken = null
    }
  },
  actions: {
    checkPhoneRegistered(_, payload) {
      const phone = normalizePhone(payload)
      if (!phone) return Promise.reject(new Error('请输入手机号'))
      return checkPhoneRegistered(phone).then(res => !!(res && res.data))
    },
    // 发送登录验证码
    sendLoginSmsCode(_, data) {
      const phone = normalizePhone(data)
      const check = canSendSms({ scene: 'login', phone })
      if (!check.ok) return Promise.reject(new Error(check.message))
      return sendLoginSmsCode({ phone }).then(res => {
        markSmsSent(check)
        return res
      })
    },

    sendRegisterSmsCode(_, data) {
      const phone = normalizePhone(data)
      const skipPhoneCheck = !!(data && data.skipPhoneCheck)
      const check = canSendSms({ scene: 'register', phone })
      if (!check.ok) return Promise.reject(new Error(check.message))
      const ensureNotRegistered = skipPhoneCheck
        ? Promise.resolve(false)
        : checkPhoneRegistered(phone).then(res => !!(res && res.data))
      return ensureNotRegistered.then(registered => {
        if (registered) return Promise.reject(new Error('手机号已注册，请直接登录'))
        return sendRegisterSmsCode({ phone }).then(sendRes => {
          markSmsSent(check)
          return sendRes
        })
      })
    },

    sendWechatBindPhoneSmsCode(_, payload) {
      const phone = normalizePhone(payload)
      const check = canSendSms({ scene: 'wechatBind', phone })
      if (!check.ok) return Promise.reject(new Error(check.message))
      return sendWechatBindPhoneSmsCode({ phone }).then(res => {
        markSmsSent(check)
        return res
      })
    },

    wechatBindPhone({ commit, state }, payload) {
      const phone = normalizePhone(payload)
      const smsCode = (payload && (payload.smsCode || payload.code)) || ''
      const tempToken = (payload && payload.tempToken) || state.wechatTempToken
      if (!String(phone || '').trim()) return Promise.reject(new Error('请输入手机号'))
      if (!String(smsCode || '').trim()) return Promise.reject(new Error('请输入验证码'))
      if (!String(tempToken || '').trim()) return Promise.reject(new Error('缺少临时token'))
      return wechatBindPhone({ phone, smsCode, tempToken }).then(async response => {
        const data = (response && response.data) || {}
        // 绑定手机号接口返回的信息就是登录成功的信息
        const token = data.token
        if (!token) {
          return Promise.reject(new Error('登录失败：未获取到token'))
        }
        
        // 保存 token
        localStorage.setItem('token', token)
        commit('SET_LOGIN_STATUS', true)
        commit('SET_WECHAT_TEMP_TOKEN', null)

        // 处理用户信息：优先使用返回数据中的用户信息
        let userInfo = null
        
        // 如果返回数据中包含 userInfo，直接使用
        if (data.userInfo) {
          userInfo = data.userInfo
        } 
        // 如果返回数据中包含用户信息字段，构建 userInfo
        else if (data.username || data.phone || data.realName || data.nickname) {
          userInfo = {
            username: data.username || '',
            phone: data.phone || phone,
            realName: data.realName || '',
            nickname: data.nickname || '',
            expireTime: data.expireTime,
            ...data
          }
        }
        
        // 如果有用户信息，保存到 localStorage 和 store
        if (userInfo) {
          localStorage.setItem('userInfo', JSON.stringify(userInfo))
          commit('SET_USER_INFO', userInfo)
          const name = userInfo.realName || userInfo.nickname || userInfo.username || userInfo.phone || phone || '用户'
          commit('SET_USERNAME', name)
        } else {
          // 如果没有用户信息，尝试从接口获取（兼容处理）
          try {
            const res = await getPersonalInfo()
            const user = (res && res.data) || {}
            localStorage.setItem('userInfo', JSON.stringify(user))
            commit('SET_USER_INFO', user)
            const name = user.realName || user.nickname || user.username || user.phone || phone || '用户'
            commit('SET_USERNAME', name)
          } catch (e) {
            // 如果获取失败，至少保存基本信息
            const basicInfo = {
              username: data.username || '',
              phone: phone,
              expireTime: data.expireTime
            }
            localStorage.setItem('userInfo', JSON.stringify(basicInfo))
            commit('SET_USER_INFO', basicInfo)
            commit('SET_USERNAME', basicInfo.username || phone || '用户')
          }
        }

        return response
      })
    },
    
    // 学员登录
    login({ commit }, loginData) {
      const phone = normalizePhone(loginData)
      const loginType =
        (loginData && loginData.loginType) ||
        ((loginData && loginData.password) ? 'password' : 'sms')
      const payload = {
        captchaCode: (loginData && loginData.captchaCode) || '',
        captchaId: (loginData && loginData.captchaId) || '',
        loginType,
        password: (loginData && loginData.password) || '',
        phone,
        smsCode: (loginData && (loginData.smsCode || loginData.code)) || ''
      }
      return login(payload).then(async response => {
        const token = response && response.data && response.data.token
        if (token) localStorage.setItem('token', token)
        commit('SET_LOGIN_STATUS', true)

        try {
          const res = await getPersonalInfo()
          const data = (res && res.data) || {}
          localStorage.setItem('userInfo', JSON.stringify(data))
          commit('SET_USER_INFO', data)
          const name = data.realName || data.nickname || data.username || data.phone || phone || '用户'
          commit('SET_USERNAME', name)
        } catch (e) {
          const userInfo = {
            username: (response && response.data && response.data.username) || '',
            expireTime: response && response.data && response.data.expireTime,
            phone
          }
          localStorage.setItem('userInfo', JSON.stringify(userInfo))
          commit('SET_USER_INFO', userInfo)
          commit('SET_USERNAME', userInfo.username || phone || '用户')
        }

        return response
      })
    },
    
    // 注册
    register({ commit }, userInfo) {
      const phone = normalizePhone(userInfo)
      if (!String(phone || '').trim()) return Promise.reject(new Error('请输入手机号'))
      const skipPhoneCheck = !!(userInfo && userInfo.skipPhoneCheck)
      const payload = {
        phone,
        password: (userInfo && userInfo.password) || '',
        smsCode: (userInfo && (userInfo.smsCode || userInfo.code)) || ''
      }
      const ensureNotRegistered = skipPhoneCheck
        ? Promise.resolve(false)
        : checkPhoneRegistered(phone).then(res => !!(res && res.data))
      return ensureNotRegistered.then(registered => {
        if (registered) return Promise.reject(new Error('账号已存在，请直接登录'))
        return register(payload)
      }).then(async response => {
        const token = response && response.data && response.data.token
        if (token) localStorage.setItem('token', token)
        commit('SET_LOGIN_STATUS', true)

        try {
          const res = await getPersonalInfo()
          const data = (res && res.data) || {}
          localStorage.setItem('userInfo', JSON.stringify(data))
          commit('SET_USER_INFO', data)
          const name = data.realName || data.nickname || data.username || data.phone || phone || '用户'
          commit('SET_USERNAME', name)
        } catch (e) {
          const info = {
            username: (response && response.data && response.data.username) || '',
            expireTime: response && response.data && response.data.expireTime,
            phone
          }
          localStorage.setItem('userInfo', JSON.stringify(info))
          commit('SET_USER_INFO', info)
          commit('SET_USERNAME', info.username || phone || '用户')
        }

        return response
      })
    },
    
    // 退出登录
    logout({ commit }) {
      return new Promise((resolve, reject) => {
        logout().then(response => {
          if (response.code === 0 || response.code === 200) {
            // 清除本地存储的token和用户信息
            localStorage.removeItem('token')
            localStorage.removeItem('userInfo')
            commit('CLEAR_USER_INFO')
            resolve(response)
          } else {
            reject(new Error(response.message || '退出登录失败'))
          }
        }).catch(error => {
          // 即使API调用失败，也要清除本地状态
          localStorage.removeItem('token')
          localStorage.removeItem('userInfo')
          commit('CLEAR_USER_INFO')
          reject(error)
        })
      })
    },
    
    // 发送忘记密码验证码
    sendForgotPasswordSmsCode(_, data) {
      const phone = normalizePhone(data)
      const check = canSendSms({ scene: 'forgot', phone })
      if (!check.ok) return Promise.reject(new Error(check.message))
      return sendForgotPasswordSmsCode({ phone }).then(res => {
        markSmsSent(check)
        return res
      })
    },
    
    // 重置密码
    resetPassword(_, data) {
      const phone = normalizePhone(data)
      const payload = {
        phone,
        smsCode: (data && (data.smsCode || data.code)) || '',
        newPassword: (data && (data.newPassword || data.password)) || ''
      }
      return resetPassword(payload)
    },
    
    // 更新用户信息
    updateUserInfo({ state, commit }, userInfo) {
      const next = { ...(state.userInfo || {}), ...(userInfo || {}) }
      commit('SET_USER_INFO', next)
      const name = next.realName || next.nickname || next.username || next.phone || next.mobile || ''
      if (name) commit('SET_USERNAME', name)
      const token = localStorage.getItem('token')
      if (token) {
        localStorage.setItem('userInfo', JSON.stringify(next))
      }
    }
  },
  getters: {
    // 获取登录状态
    isLoggedIn: state => state.isLoggedIn,
    // 获取用户信息
    userInfo: state => state.userInfo,
    // 获取用户名
    username: state => state.username,
    isRealNameVerified: state => {
      const info = state.userInfo || {}
      const value = info.isRealNameVerified
      if (value === true || value === 1 || value === '1') return true
      if (value === false || value === 0 || value === '0') return false
      const alt = info.is_real_name_verified
      if (alt === true || alt === 1 || alt === '1') return true
      if (alt === false || alt === 0 || alt === '0') return false
      const legacy = info.realNameVerified
      if (legacy === true || legacy === 1 || legacy === '1') return true
      if (legacy === false || legacy === 0 || legacy === '0') return false
      return false
    },
    wechatTempToken: state => state.wechatTempToken
  }
}

export default auth
